/**
 * @file useProductCard.ts
 * @description Состояние и действия карточки товара (вынесено из
 * `ProductEditPage` в phase19.31, когда страница перешла на шапку кабинета и
 * перевалила за 200 строк). Логика сохранения — прежняя (phase08.5 / W22):
 * проверки до запроса, создание или правка, переход на адрес нового товара.
 *
 * Новое в phase19.31: «В архив» — с подтверждением окном кабинета. Ответ
 * загрузки товара принимается, только если адрес не сменился (переход A → B
 * не подменяет карточку B поздним ответом A).
 *
 * «Есть несохранённые изменения» — сравнение формы с загруженными
 * значениями (`productFormChanged`), а не флаг «кто-то вызвал onChange»:
 * поле, которое при загрузке само нормализует значение, больше не делает
 * карточку «изменённой» (приёмка QA-5 D13). Вернул как было — изменений нет.
 */

import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router";

import { PRODUCT_KIND_REQUIRED_TEXT } from "../../../../components/catalog/product-kinds/ProductKindField";
import { useProductKinds } from "../../../../components/catalog/product-kinds/useProductKinds";
import { useNotify } from "../../../../components/ui/NotifyProvider";
import { useConfirm } from "../../../../components/ui/use-confirm";
import { catalogCategories, catalogProducts, type CatalogCategory, type CatalogProduct } from "../../../../lib/api-catalog";
import { ApiError } from "../../../../lib/api-client";
import { resolveCategoryKind } from "../../../../lib/product-kind-resolve";
import { attributesError } from "../product-attributes";
import { fromProduct, productFormChanged, toPayload, type ProductFormState } from "../product-form";
import { notifyRedirectChange } from "../../../../lib/site-seo/redirect-change";

export function useProductCard(slug: string | undefined, id: string | undefined, presetCategoryId: string) {
  const navigate = useNavigate();
  const notify = useNotify();
  const confirmApi = useConfirm();
  // phase19.22 (W22) — справочник видов: поле «Вид товара» и проверка «вид обязателен».
  const productKinds = useProductKinds();
  const isNew = id === "new" || !id;
  const [product, setProduct] = useState<CatalogProduct | null>(null);
  const [categories, setCategories] = useState<CatalogCategory[]>([]);
  const [form, setForm] = useState<ProductFormState>(() => ({ ...fromProduct(null), categoryId: presetCategoryId }));
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  // Загруженные значения: «есть изменения» — сравнением с ними (QA-5 D13).
  const [baseline, setBaseline] = useState<ProductFormState>(form);
  const dirty = productFormChanged(form, baseline);

  useEffect(() => {
    catalogCategories
      .list()
      .then(setCategories)
      .catch(() => setCategories([]));
  }, []);

  useEffect(() => {
    if (isNew || !id) {
      setProduct(null);
      // Новый товар — подставляем выбранную в реестре категорию (?category=).
      const blank = { ...fromProduct(null), categoryId: presetCategoryId };
      setForm(blank);
      setBaseline(blank);
      return;
    }
    // Переход с товара A на товар B: поздний ответ A не должен подменить B.
    let current = true;
    setLoading(true);
    setError(null);
    catalogProducts
      .findOne(id)
      .then((p) => {
        if (!current) return;
        setProduct(p);
        setForm(fromProduct(p));
        setBaseline(fromProduct(p));
        setSavedAt(new Date(p.updatedAt));
      })
      .catch((err: unknown) => {
        if (current) setError(err instanceof ApiError ? err.message : "Не удалось загрузить товар");
      })
      .finally(() => {
        if (current) setLoading(false);
      });
    return () => {
      current = false;
    };
  }, [id, isNew, presetCategoryId]);

  const patch = useCallback((p: Partial<ProductFormState>) => {
    setForm((f) => ({ ...f, ...p }));
  }, []);

  /** Имя вида, который даст категория (с учётом предков); null — вида нет. */
  function categoryKindName(categoryId: string): string | null {
    const byId = new Map(categories.map((c) => [c.id, c]));
    return resolveCategoryKind(categoryId, byId, productKinds.kindsById).kind?.name ?? null;
  }

  async function save() {
    setError(null);
    if (!form.categoryId) return setError("Выберите категорию.");
    if (!form.displayName.trim()) return setError("Название обязательно.");
    // Характеристики (SB33): строка без названия или значения, повтор названия.
    const attrError = attributesError(form.attributes);
    if (attrError) {
      notify.error(attrError);
      return setError(attrError);
    }
    // W22: новый товар без своего вида и без вида у категории сервер не примет
    // (422 product_kind_required) — говорим об этом до запроса. Справочник не
    // загрузился — не гадаем, решит сервер.
    const kindsReady = !productKinds.loading && !productKinds.error;
    if (isNew && kindsReady && !form.productKindId && !categoryKindName(form.categoryId)) {
      return setError(PRODUCT_KIND_REQUIRED_TEXT);
    }
    setSaving(true);
    try {
      const payload = toPayload(form, product);
      const saved = product ? await catalogProducts.update(product.id, payload) : await catalogProducts.create(payload);
      setProduct(saved);
      setForm(fromProduct(saved));
      setBaseline(fromProduct(saved));
      setSavedAt(new Date());
      notify.success(product ? "Сохранено" : "Товар создан");
      // Р3в (SB131): переадресация со старого адреса создана или снята — тостом.
      notifyRedirectChange(saved.redirect, notify);
      if (isNew) navigate(`/shop/${slug}/catalog/products/${saved.id}`, { replace: true });
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : "Не удалось сохранить";
      setError(msg);
      notify.error(msg);
    } finally {
      setSaving(false);
    }
  }

  async function togglePublish() {
    if (!product) return;
    const target = product.status === "ACTIVE" ? "DRAFT" : "ACTIVE";
    try {
      setProduct(await catalogProducts.updateStatus(product.id, target));
      notify.success(target === "ACTIVE" ? "Товар опубликован" : "Товар снят с публикации");
    } catch (err) {
      notify.error(err instanceof ApiError ? err.message : "Не удалось изменить публикацию");
    }
  }

  async function archive() {
    if (!product) return;
    const ok = await confirmApi.confirm({
      title: `Отправить «${product.displayName}» в архив?`,
      description: "Товар пропадёт с сайта и из новых заказов. История продаж и движений сохранится.",
      action: "В архив",
      danger: true,
    });
    if (!ok) return;
    try {
      setProduct(await catalogProducts.archive(product.id));
      notify.success("Товар отправлен в архив");
    } catch (err) {
      notify.error(err instanceof ApiError ? err.message : "Не удалось отправить в архив");
    }
  }

  function discard() {
    if (!product) return;
    setForm(baseline);
  }

  return {
    isNew,
    product,
    categories,
    form,
    patch,
    loading,
    saving,
    error,
    savedAt,
    dirty,
    kinds: productKinds.kinds,
    save,
    togglePublish,
    archive,
    discard,
    confirmRequest: confirmApi.request,
    settleConfirm: confirmApi.settle,
  };
}

export type ProductCardState = ReturnType<typeof useProductCard>;
